import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useMutatedObservationsRefresh } from '../../../stores/buildScenarioStore'
import useBuildScenarioStore from '../../../stores/buildScenarioStore'
import { useGlobalOverlay } from '../../../../../../context/GlobalOverlayContext'
import * as d3 from 'd3'

const IndependentDriverPlotDebug = ({
  hookRef,
  scenarioId,
  driverId,
  isVisible,
  id,
}) => {
  const mutatedObservationsRefreshId = useMutatedObservationsRefresh()
  const [currentRefreshId, setCurrentRefreshId] = useState(0)
  const [localMutatedObservations, setLocalMutatedObservations] = useState(null)
  const [fixedScales, setFixedScales] = useState(null) // Store fixed axis scales
  const svgRef = useRef(null)
  const { addOverlay, removeOverlay } = useGlobalOverlay()

  // Get drivers for name lookup
  const drivers = useBuildScenarioStore((state) => state.drivers)

  // Create stable overlay key based on scenario and driver ID
  const overlayKey = useMemo(() => {
    return `debug-plot-${scenarioId}-${driverId}-${id}`
    /* eslint-disable-next-line */
  }, [scenarioId, driverId])

  // Effect to take snapshot when mutatedObservationsRefreshId changes
  useEffect(() => {
    if (mutatedObservationsRefreshId !== currentRefreshId) {
      // Take a local copy of the specific driver's mutated observations
      const scenario = useBuildScenarioStore
        .getState()
        .scenarios.find((s) => s.id === scenarioId)
      const driver = scenario?.driverValues?.find((d) => d.id === driverId)

      if (driver?.mutatedObservations) {
        const newObservations = {
          observationValues: [...driver.mutatedObservations.observationValues],
          observationTimecodes: [
            ...driver.mutatedObservations.observationTimecodes,
          ],
        }

        setLocalMutatedObservations(newObservations)
        setCurrentRefreshId(mutatedObservationsRefreshId)

        // Set fixed scales on first data update
        if (!fixedScales && newObservations.observationValues.length > 0) {
          const allData = newObservations.observationValues.map(
            (value, index) => ({
              date: new Date(newObservations.observationTimecodes[index]),
              value: value,
            }),
          )

          const chartWidth = 250 - 50 - 25 // width - left margin - right margin
          const chartHeight = 150 - 25 - 40 // height - top margin - bottom margin

          const xScale = d3
            .scaleTime()
            .domain(d3.extent(allData, (d) => d.date))
            .range([0, chartWidth])

          const yScale = d3
            .scaleLinear()
            .domain(d3.extent(allData, (d) => d.value))
            .nice()
            .range([chartHeight, 0])

          setFixedScales({ xScale, yScale })
        }
      }
    }
    /* eslint-disable-next-line */
  }, [mutatedObservationsRefreshId, fixedScales])

  // Memoize the draw function
  const drawMiniChart = useCallback(
    (svgElement) => {
      if (
        !localMutatedObservations ||
        !drivers.length ||
        !svgElement ||
        !fixedScales
      )
        return

      const svg = d3.select(svgElement)
      svg.selectAll('*').remove()

      // Get the actual container dimensions
      const containerRect = svgElement.getBoundingClientRect()
      const width = containerRect.width || 250
      const height = containerRect.height || 150
      const margin = { top: 10, right: 25, bottom: 40, left: 50 }
      const chartWidth = width - margin.left - margin.right
      const chartHeight = height - margin.top - margin.bottom

      svg.attr('width', width).attr('height', height)

      const g = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`)

      // Process data for D3
      const allData = localMutatedObservations.observationValues.map(
        (value, index) => ({
          driverId: driverId,
          date: new Date(localMutatedObservations.observationTimecodes[index]),
          value: value,
          driverName:
            drivers.find((d) => d.id === driverId)?.name ||
            `Driver ${driverId}`,
        }),
      )

      if (allData.length === 0) return

      // Use the fixed scales from the first data instead of creating new ones
      // Update the range to match current container dimensions
      const xScale = fixedScales.xScale.copy().range([0, chartWidth])
      const yScale = fixedScales.yScale.copy().range([chartHeight, 0])

      // Create line generator
      const line = d3
        .line()
        .x((d) => xScale(d.date))
        .y((d) => yScale(d.value))
        .curve(d3.curveMonotoneX)

      // Add both X-axis (bottom) and Y-axis (left)
      g.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(xScale).ticks(3).tickFormat(d3.timeFormat('%b')))

      g.append('g').attr('class', 'y-axis').call(d3.axisLeft(yScale).ticks(3))

      // Draw line
      g.append('path')
        .datum(allData)
        .attr('fill', 'none')
        .attr('stroke', '#2196F3')
        .attr('stroke-width', 1.5)
        .attr('d', line)

      // Add circles for data points
      g.selectAll('.circle')
        .data(allData)
        .enter()
        .append('circle')
        .attr('class', 'circle')
        .attr('cx', (d) => xScale(d.date))
        .attr('cy', (d) => yScale(d.value))
        .attr('r', 2)
        .attr('fill', '#2196F3')

      // Removed the duplicate title since we now have "DEBUGGING" in the container
    },
    /* eslint-disable-next-line */
    [
      localMutatedObservations,
      drivers,
      driverId,
      currentRefreshId,
      fixedScales,
    ],
  )

  // Create the overlay JSX element
  const overlayElement = useMemo(() => {
    return (
      <div
        style={{
          width: '300px',
          height: '200px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid #c0c0c0',
          borderRadius: '8px',
          padding: '15px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease-out, visibility 0.3s ease-out',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginBottom: '10px',
            color: '#333',
            textAlign: 'center',
          }}
        >
          CHANGING
        </div>

        <svg
          ref={svgRef}
          style={{
            width: '100%',
            height: 'calc(100% - 35px)',
          }}
        />
      </div>
    )
    /* eslint-disable-next-line */
  }, [isVisible, driverId, currentRefreshId])

  // Effect to manage overlay visibility
  useEffect(() => {
    if (isVisible && hookRef?.current) {
      addOverlay(overlayKey, overlayElement, hookRef.current, 'top')
    } else {
      removeOverlay(overlayKey)
    }
  }, [
    isVisible,
    overlayKey,
    hookRef,
    addOverlay,
    removeOverlay,
    overlayElement,
  ])

  // Effect to redraw chart when data changes
  useEffect(() => {
    if (isVisible && svgRef.current && localMutatedObservations) {
      drawMiniChart(svgRef.current)
    }
  }, [isVisible, currentRefreshId, localMutatedObservations, drawMiniChart])

  // Cleanup effect
  useEffect(() => {
    return () => {
      removeOverlay(overlayKey)
    }
  }, [overlayKey, removeOverlay])

  // Don't render anything directly - overlay is handled by GlobalOverlayProvider
  return null
}

export default IndependentDriverPlotDebug
